import {
  ColDef,
  ICellRendererParams,
  SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  CELL_CLASSES,
  TableContainer,
  TableContent,
  TableHeader,
  ROW_SELECTION,
  TABLE_LAYOUT_OPTIONS,
  TableHeaderProps,
} from "@cdkglobal/radial";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { useMemo, useRef, useState } from "react";
import { GenericSearchResponseObject } from "../../../../../types";
import { useIntl } from "react-intl";

// Replaces deprecated TextRenderer from cdk-radial
const TextCellRenderer = ({ value }: ICellRendererParams) => (
  <span>{value ?? ""}</span>
);

export default function CopyGroupCodesTable({
  headerTitle,
  responseRowData,
  setSelectedRows,
}: Readonly<{
  headerTitle: string;
  responseRowData: GenericSearchResponseObject[];
  setSelectedRows: (rows: GenericSearchResponseObject[]) => void;
}>): JSX.Element {
  const gridRef = useRef<AgGridReact>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const intl = useIntl();

  const handleSelectionChange = (event: SelectionChangedEvent) => {
    const selectedRows = event.api.getSelectedRows();
    setSelectedRows(selectedRows);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
  };

  const filteredResponse = useMemo(() => {
    return responseRowData.filter((row) =>
      Object.values(row).some((cellValue) =>
        cellValue
          ?.toString()
          .toLowerCase()
          .includes(searchInputValue.toLowerCase()),
      ),
    );
  }, [searchInputValue, JSON.stringify(responseRowData)]);

  const headerProps: TableHeaderProps = {
    className: "import-group-codes-table-header",
    dataTestId: "import-group-codes-table-header",
    headerTitle: headerTitle,
    hideBorder: false,
    hideTableTitle: false,
    id: `import-group-codes-table-header`,
    isAlwaysExpanded: false,
    isDownloadable: false,
    isPrintable: false,
    isSearchable: true,
    searchInputValue: searchInputValue,
    onSearchInputChange: handleSearchInputChange,
    onSearchInputClear: () => setSearchInputValue(""),
    overflowButtonProps: undefined,
    searchInputName: "import-group-codes-table-search-input-name",
    searchPlaceholder: "Search all columns",
  };

  const columns: ColDef[] = useMemo(
    () => [
      {
        cellClass: CELL_CLASSES.CHECKBOX,
        field: "check",
        filter: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        headerClass: [CELL_CLASSES.CHECKBOX, CELL_CLASSES.SELECT_ALL],
        headerName: "",
        maxWidth: 55,
        minWidth: 55,
      },
      {
        cellRenderer: TextCellRenderer,
        field: "group_code_id",
        headerName: intl.formatMessage({
          id: "tableheaders.group_code_id",
          defaultMessage: "Group Code ID",
        }),
      },
      {
        cellRenderer: TextCellRenderer,
        field: "group_code_description",
        headerName: intl.formatMessage({
          id: "tableheaders.group_code_description",
          defaultMessage: "Group Code Description",
        }),
        minWidth: 450,
      },
    ],
    [],
  );

  return (
    <TableContainer agGridVersion="31.3.4">
      <TableHeader {...headerProps} hideBorder headerTitle={headerTitle} />
      <TableContent data-testid="table-content">
        <AgGridReact
          className="ag-theme-balham"
          containerStyle={{ height: "45vh", width: "100%" }}
          rowData={filteredResponse}
          columnDefs={columns}
          domLayout={TABLE_LAYOUT_OPTIONS.NORMAL}
          rowSelection={ROW_SELECTION.MULTIPLE}
          suppressRowClickSelection
          defaultColDef={{
            filter: true,
            sortable: true,
            resizable: true,
            flex: 1,
            autoHeight: true,
            wrapText: true,
          }}
          ref={gridRef}
          onSelectionChanged={handleSelectionChange}
        />
      </TableContent>
    </TableContainer>
  );
}
